import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChatIcon } from '@/components/embed-popup/chat-icon';
import { cn } from '@/lib/utils';
const DEFAULT_TEASER_MESSAGES = [
    { text: 'Hi there! 👋 How can I help you today?', delay: 3000 },
];
const DEFAULT_TYPING_DURATION_MS = 2000;
function playNotificationSound() {
    if (typeof window === 'undefined')
        return;
    try {
        const ctx = new AudioContext();
        const now = ctx.currentTime;
        // Two-tone chime: C5 → E5
        const frequencies = [523.25, 659.25];
        frequencies.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.3, now + i * 0.15);
            gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now + i * 0.15);
            osc.stop(now + i * 0.15 + 0.4);
        });
    }
    catch (_a) {
        // AudioContext not available — silently skip
    }
}
function TypingDots() {
    return (_jsxs("div", { className: "flex items-center gap-[5px] px-1 py-0.5", children: [_jsx("span", { className: "typing-dot" }), _jsx("span", { className: "typing-dot" }), _jsx("span", { className: "typing-dot" })] }));
}
function TeaserBubble({ messages, typingDuration, onDismiss, onClick, onFirstMessage, }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [phase, setPhase] = useState('typing');
    const timerRef = useRef(undefined);
    const scheduleNext = useCallback(() => {
        const nextIndex = currentIndex + 1;
        if (nextIndex >= messages.length) {
            // No more messages — auto-dismiss after 12s
            timerRef.current = setTimeout(onDismiss, 12000);
            return;
        }
        // Wait the next message's delay, then show typing for it
        timerRef.current = setTimeout(() => {
            setPhase('typing');
            setCurrentIndex(nextIndex);
        }, messages[nextIndex].delay);
    }, [currentIndex, messages, onDismiss]);
    // Handle phase transitions
    useEffect(() => {
        if (phase === 'typing') {
            // Show typing dots, then reveal the message
            timerRef.current = setTimeout(() => {
                setPhase('message');
            }, typingDuration);
        }
        else if (phase === 'message') {
            // Message is visible — play notification and schedule the next one
            playNotificationSound();
            if (currentIndex === 0) {
                onFirstMessage === null || onFirstMessage === void 0 ? void 0 : onFirstMessage();
            }
            scheduleNext();
        }
        else if (phase === 'waiting') {
            // Dismissed current message — schedule next one
            const nextIndex = currentIndex + 1;
            if (nextIndex < messages.length) {
                timerRef.current = setTimeout(() => {
                    setCurrentIndex(nextIndex);
                    setPhase('typing');
                }, messages[nextIndex].delay);
            }
        }
        return () => clearTimeout(timerRef.current);
    }, [phase, currentIndex, typingDuration, scheduleNext, messages, onFirstMessage]);
    const handleDismissCurrent = (e) => {
        e.stopPropagation();
        clearTimeout(timerRef.current);
        const nextIndex = currentIndex + 1;
        if (nextIndex >= messages.length) {
            // Last message — fully dismiss
            onDismiss();
        }
        else {
            // More messages remain — hide bubble, wait for next
            setPhase('waiting');
        }
    };
    const currentMessage = messages[currentIndex];
    const isVisible = phase !== 'waiting';
    return (_jsxs(motion.div, { initial: { opacity: 0, y: 10, scale: 0.9 }, animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 10, scale: isVisible ? 1 : 0.9 }, exit: { opacity: 0, y: 10, scale: 0.9 }, transition: { type: 'spring', stiffness: 400, damping: 30 }, onClick: onClick, className: cn('fixed z-[9999] cursor-pointer', 'max-w-[240px] rounded-2xl rounded-br-sm', 'shadow-[0_4px_14px_rgba(0,0,0,0.15)]', !isVisible && 'pointer-events-none'), style: {
            right: 'calc(var(--lk-right-margin, 16px) + 8px)',
            bottom: 'calc(var(--lk-bottom-margin, 16px) + 64px)',
            backgroundColor: 'var(--lk-button-color, #7C3AED)',
        }, children: [_jsx("button", { onClick: handleDismissCurrent, className: "absolute -top-2 -right-2 flex size-5 items-center justify-center rounded-full bg-gray-700 text-[10px] text-white shadow-md hover:bg-gray-600", "aria-label": "Dismiss", children: "\u2715" }), _jsx("div", { className: "px-4 py-3", children: _jsx(AnimatePresence, { mode: "wait", children: phase === 'typing' ? (_jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, children: _jsx(TypingDots, {}) }, `typing-${currentIndex}`)) : phase === 'message' ? (_jsx(motion.p, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 }, className: "m-0 text-[14px] leading-relaxed text-white", children: currentMessage.text }, `message-${currentIndex}`)) : null }) })] }));
}
export function Trigger({ popupOpen, onToggle, appConfig }) {
    var _a, _b, _c, _d;
    const teaserMessages = (_a = appConfig.teaserMessages) !== null && _a !== void 0 ? _a : DEFAULT_TEASER_MESSAGES;
    const typingDuration = (_b = appConfig.typingDuration) !== null && _b !== void 0 ? _b : DEFAULT_TYPING_DURATION_MS;
    const firstDelay = (_d = (_c = teaserMessages[0]) === null || _c === void 0 ? void 0 : _c.delay) !== null && _d !== void 0 ? _d : 3000;
    const [showTeaser, setShowTeaser] = useState(false);
    const [teaserDismissed, setTeaserDismissed] = useState(false);
    const [showBadge, setShowBadge] = useState(false);
    // Show teaser bubble after the first message's delay
    useEffect(() => {
        if (popupOpen || teaserDismissed || teaserMessages.length === 0)
            return;
        const timer = setTimeout(() => {
            setShowTeaser(true);
        }, firstDelay);
        return () => clearTimeout(timer);
    }, [popupOpen, teaserDismissed, firstDelay, teaserMessages.length]);
    // Hide teaser and badge when popup opens
    useEffect(() => {
        if (popupOpen) {
            setShowTeaser(false);
            setTeaserDismissed(true);
            setShowBadge(false);
        }
    }, [popupOpen]);
    const handleTeaserDismiss = () => {
        setShowTeaser(false);
        setTeaserDismissed(true);
    };
    const handleTeaserClick = () => {
        setShowTeaser(false);
        setTeaserDismissed(true);
        onToggle();
    };
    // Hide the trigger button when popup is open (close button is now in header)
    if (popupOpen) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(AnimatePresence, { children: showTeaser && !popupOpen && (_jsx(TeaserBubble, { messages: teaserMessages, typingDuration: typingDuration, onDismiss: handleTeaserDismiss, onClick: handleTeaserClick, onFirstMessage: () => setShowBadge(true) }, "teaser-bubble")) }), _jsx(AnimatePresence, { children: _jsxs(motion.button, { initial: { scale: 0, opacity: 0 }, animate: { scale: 1, opacity: 1 }, exit: { scale: 0, opacity: 0 }, whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 }, transition: {
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                    }, onClick: onToggle, style: {
                        right: 'var(--lk-right-margin, 24px)',
                        bottom: 'var(--lk-bottom-margin, 24px)',
                        backgroundColor: 'var(--lk-button-color, #7C3AED)',
                    }, className: cn('fixed z-[9999] flex size-14 cursor-pointer items-center justify-center rounded-xl border-none p-2 outline-none', 'shadow-2xl', 'transition-all duration-300', 'hover:shadow-[0_8px_30px_rgba(124,58,237,0.4)] hover:brightness-90', 'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2'), "aria-label": "Open chat", children: [_jsx(ChatIcon, { size: 34 }), _jsx("span", { className: "absolute -top-0.5 -right-0.5 size-3 rounded-sm bg-green-500" }), showBadge && (_jsx("span", { className: "absolute -top-2 -left-2 flex size-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white shadow-md", children: "1" }))] }, "trigger-button") })] }));
}
